import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bot, Heart, Sparkles, Zap, Radar, ScrollText } from "lucide-react";
import { api, formatApiErrorDetail } from "@/context/AuthContext";
import { MatchChat } from "@/components/dashboard/MatchChat";
import { triggerMatchAlert } from "@/lib/matchAlert";

export const MatchAlerts = ({ t, lang }) => {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/matches");
    setMatches(data.filter((m) => m.interaction === "agent_dialogue"));
  }, []);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/matchmaking/run");
      if (data.match_found) {
        toast.success(t.alertTitle);
        await load();
      } else {
        toast.info(t.noMatchFound);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRunning(false);
    }
  };

  const like = async (id) => {
    try {
      const { data } = await api.post(`/matches/${id}/like`);
      setMatches((ms) => ms.map((m) => (m._id === id ? { ...m, status: data.status, liked_by_me: true } : m)));
      if (data.status === "Mutual Connection") {
        triggerMatchAlert(t.mutualAlertTitle, t.mutualConn);
        toast.success(t.mutualConn);
      } else {
        toast.success(t.pendingConf);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <section className="mt-10" data-testid="match-alerts-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="font-heading text-lg font-black flex items-center gap-2">
          <Zap size={18} className="text-[#00FFAA]" /> {t.matchesTitle}
        </h2>
        <button data-testid="run-matchmaking-btn" onClick={run} disabled={running}
          className="px-5 py-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#00F5FF]/10 border border-[#00F5FF]/40 text-[#00F5FF] hover:bg-[#00F5FF]/20 transition-colors disabled:opacity-60 flex items-center gap-2">
          <Radar size={13} className={running ? "animate-spin" : ""} />
          {running ? t.matchmaking : t.runMatchmaking}
        </button>
      </div>

      {matches.length === 0 && <p className="text-xs text-white/30">{t.noMatches}</p>}

      <div className="space-y-5">
        {matches.map((m) => (
          <div key={m._id} data-testid="match-alert-card"
            className="relative bg-[#0F0F1A]/80 border border-[#00FFAA]/25 rounded-3xl p-6 overflow-hidden shadow-[0_0_30px_rgba(0,255,170,0.08)]">
            <div className="absolute -top-16 -right-16 w-48 h-48 bg-[#00FFAA] blur-[100px] opacity-10 pointer-events-none" />
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <p className="font-heading font-black text-sm text-[#00FFAA] flex items-center gap-2">
                <Sparkles size={16} /> {t.alertTitle}
              </p>
              <span className="text-[10px] font-bold tracking-widest px-3 py-1.5 rounded-full bg-[#B026FF]/15 border border-[#B026FF]/40 text-[#D080FF]">
                {t.compatibility}: {m.compatibility}%
              </span>
            </div>
            <p className="text-xs text-white/50 mb-4">
              <Bot size={13} className="inline mr-1 text-[#00F5FF]" />
              {m.other?.avatar_name || m.other?.name} · {t.sharedObj}: {(m.shared_objectives || []).join(", ")}
            </p>

            <p className="text-[10px] uppercase tracking-[0.2em] text-white/35 font-bold mb-2">{t.transcript}</p>
            <div className="space-y-2 mb-4">
              {(m.transcript || []).map((turn, i) => (
                <div key={i} className={`text-xs p-3 rounded-xl border max-w-[92%] ${
                  i % 2 === 0
                    ? "bg-[#B026FF]/10 border-[#B026FF]/20 text-white/85"
                    : "bg-[#00F5FF]/10 border-[#00F5FF]/20 text-white/85 ml-auto"
                }`}>
                  <span className={`font-bold ${i % 2 === 0 ? "text-[#D080FF]" : "text-[#00F5FF]"}`}>{turn.sender || turn.speaker}: </span>
                  {turn.text}
                </div>
              ))}
            </div>

            <div className="bg-[#00FFAA]/5 border border-[#00FFAA]/30 rounded-xl p-4 mb-5" data-testid="analysis-point">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#00FFAA] font-bold mb-1">{t.analysisLabel}</p>
              <p className="text-xs text-white/80 italic">"{m.analysis_point}"</p>
            </div>

            <button data-testid="view-transcript-btn" onClick={() => navigate(`/match/${m._id}`)}
              className="mb-4 mr-3 px-5 py-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#00F5FF]/10 border border-[#00F5FF]/40 text-[#00F5FF] hover:bg-[#00F5FF]/20 transition-colors inline-flex items-center gap-2">
              <ScrollText size={13} /> {t.viewFull}
            </button>

            {m.liked_by_me || m.status === "Pending User Confirmation" || m.status === "Mutual Connection" ? (
              <>
                <span data-testid="match-status-badge" className={`inline-flex items-center gap-2 text-[10px] font-bold tracking-widest px-4 py-2 rounded-full ${
                  m.status === "Mutual Connection"
                    ? "bg-[#00FFAA]/15 border border-[#00FFAA]/50 text-[#00FFAA]"
                    : "bg-white/5 border border-white/15 text-white/50"
                }`}>
                  <Heart size={13} className={m.liked_by_me ? "fill-current" : ""} />
                  {m.status === "Mutual Connection" ? t.mutualConn : t.pendingConf}
                </span>
                {m.status === "Mutual Connection" && <MatchChat matchId={m._id} lang={lang} />}
              </>
            ) : (
              <button data-testid="like-connect-btn" onClick={() => like(m._id)}
                className="px-6 py-3 rounded-full text-[10px] font-bold uppercase tracking-widest bg-gradient-to-r from-[#00FFAA] to-[#00F5FF] text-[#05050A] shadow-[0_0_20px_rgba(0,255,170,0.35)] hover:shadow-[0_0_30px_rgba(0,255,170,0.55)] transition-shadow flex items-center gap-2">
                <Heart size={13} /> {t.likeConnect}
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};
